import { BibDocumentoModel } from '@/models/biblioteca/bibdocumento/bibdocumento.model';
import { BibDocumentoPesquisaModel } from '@/models/biblioteca/bibdocumento/bibdocumentopesquisa.model';
import { BibDocumentoPesquisaPalavraChaveModel } from '@/models/biblioteca/bibdocumento/bibdocumentopesquisapalavrachave.model';
import api from '@/services/axios/api';
import { AxiosResponse } from 'axios';
import { DataSource, FiltrosModel } from '@/datasources/consulte-grid';
import { RegisterOutput } from '@/models/register-output';
import { useStore } from '@/store';
import router from '@/routes';
import { BibDocumentoMarcModel } from '@/models/biblioteca/bibdocumento/bibdocumentomarc.model';

/**
 * Resposta dos endpoints de capa.
 *
 * Campos com NOME, e não uma string solta, por um motivo concreto: o `ApiBaseController` da API
 * sobrescreve `Ok(string)` e embrulha texto em `{ mensagem: "..." }`. Enquanto o endpoint
 * devolvia string, o front gravava esse objeto inteiro dentro de `documento.capa` — o POST do
 * cadastro ia com `capa: { mensagem: "16-capa.jpg" }` e voltava 400, e o `<img src>` recebia um
 * objeto em vez da URL.
 */
export interface CapaDocumentoResposta {
  /** Caminho gravado em `BibDocumento.CAPA`. Vazio quando a capa veio do binário antigo. */
  capa: string;
  /** URL assinada do Azure, ou um `data:` URI quando a capa é a antiga. */
  url: string;
  /**
   * De onde a imagem veio: `azure` ou `legado`.
   *
   * A tela usa para avisar quando o que está sendo exibido ainda é a capa antiga — sem isso,
   * "a capa que subi não aparece" e "a capa não subiu" são indistinguíveis.
   */
  origem: string;
}

class BibDocumentoService {
  /**
   * Envia a IMAGEM DE CAPA do documento.
   *
   * Vai para o Azure pelo mesmo caminho dos demais arquivos da biblioteca; o servidor grava só
   * o CAMINHO em `BibDocumento.CAPA`. Devolve esse caminho.
   */
  async enviarCapa(codigoDocumento: number, arquivo: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', arquivo, arquivo.name);

    const resposta = await api.post<CapaDocumentoResposta>(
      `/BibDocumento/${codigoDocumento}/Capa`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    // Lê o CAMPO, não o corpo. O corpo cru já foi parar dentro de `documento.capa` uma vez —
    // ver o comentário de `CapaDocumentoResposta`.
    return resposta.data?.capa ?? '';
  }

  /**
   * A URL (assinada, temporária) da capa.
   *
   * Pedida no momento de exibir, e nunca guardada: a assinatura do Azure expira em 30 dias.
   * Documento sem capa responde 204, e o Axios entrega string vazia.
   */
  async obterCapa(codigoDocumento: number): Promise<CapaDocumentoResposta> {
    const resposta = await api.get<CapaDocumentoResposta>(
      `/BibDocumento/${codigoDocumento}/Capa`,
    );

    return resposta.data ?? { capa: '', url: '', origem: '' };
  }

  async obterUrlDaCapa(codigoDocumento: number): Promise<string> {
    return (await this.obterCapa(codigoDocumento)).url;
  }

  async excluirCapa(codigoDocumento: number): Promise<void> {
    await api.delete(`/BibDocumento/${codigoDocumento}/Capa`);
  }

  dadosIndexPaginado(
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[]
  ): Promise<AxiosResponse<DataSource<BibDocumentoModel>>> {
    return api.post<DataSource<BibDocumentoModel>>('/BibDocumento/RetornoPaginado', {
      rowsPerPage: itensPorPagina,
      pageNumber: pagina,
      filters: filters
    });
  }

  dadosPesquisa(
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[]
  ) {
    const route = router.currentRoute.value;
    const isCentral = route.path.endsWith('/0');
    const codigoEscola = isCentral ? 0 : useStore().getters.getCodigoEscola;
    return api.post<DataSource<BibDocumentoPesquisaModel>>(
      `/BibDocumento/RetornoPesquisa/${codigoEscola}`,
      {
        rowsPerPage: itensPorPagina,
        pageNumber: pagina,
        filters
      }
    );
  }

  dadosPesquisaPorEscola(
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[],
    codigoEscola: number
  ) {
    return api.post<DataSource<BibDocumentoPesquisaModel>>(
      `/BibDocumento/RetornoPesquisa/${Number(codigoEscola || 0)}`,
      {
        rowsPerPage: itensPorPagina,
        pageNumber: pagina,
        filters
      }
    );
  }

  async pesquisarParaReserva(
    termo: string,
    codigoEscola: number,
    incluirDisponiveis = false,
    limite = 50
  ): Promise<BibDocumentoPesquisaModel[]> {
    const termoNormalizado = (termo ?? '').trim();
    const criarFiltros = (campoPesquisa?: string) => {
      const filters: FiltrosModel[] = [];

      const filtroTemExemplar = new FiltrosModel('TemExemplarParaEmprestimo');
      filtroTemExemplar.addFilter(
        'TemExemplarParaEmprestimo',
        'true',
        'pt-Br',
        'equals',
        'equals',
        'TemExemplarParaEmprestimo'
      );
      filters.push(filtroTemExemplar);

      const filtroCircula = new FiltrosModel('Circula');
      filtroCircula.addFilter(
        'Circula',
        '1',
        'pt-Br',
        'equals',
        'equals',
        'Circula'
      );
      filters.push(filtroCircula);

      if (!incluirDisponiveis) {
        const filtroIndisponivel = new FiltrosModel('ExemplaresDisponiveis');
        filtroIndisponivel.addFilter(
          'ExemplaresDisponiveis',
          '0',
          'pt-Br',
          'equals',
          'equals',
          'ExemplaresDisponiveis'
        );
        filters.push(filtroIndisponivel);
      }

      if (termoNormalizado && campoPesquisa) {
        const filtroPesquisa = new FiltrosModel(campoPesquisa);
        filtroPesquisa.addFilter(campoPesquisa, termoNormalizado, 'pt-Br', 'contains', 'contains', campoPesquisa);
        filters.push(filtroPesquisa);
      }

      return filters;
    };

    const porTitulo = (await this.dadosPesquisaPorEscola(
      0,
      limite,
      criarFiltros(termoNormalizado ? 'titulo' : undefined),
      codigoEscola
    )).data.rows ?? [];

    if (porTitulo.length || !termoNormalizado) return porTitulo;

    return (await this.dadosPesquisaPorEscola(
      0,
      limite,
      criarFiltros('autor'),
      codigoEscola
    )).data.rows ?? [];
  }

  dadosPalavraChave(
    linhaPesquisa: string,
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[]
  ) {
    const linhaPesquisaEncoded = encodeURIComponent(linhaPesquisa ?? '');
    return api.post<DataSource<BibDocumentoPesquisaPalavraChaveModel>>(
      `/BibDocumento/RetornoPalavraChave/${linhaPesquisaEncoded}`,
      {
        rowsPerPage: itensPorPagina,
        pageNumber: pagina,
        filters
      }
    );
  }

  inserirAtualizar(bibDocumento: BibDocumentoModel): Promise<AxiosResponse<RegisterOutput>> {
    return api.post('/BibDocumento', bibDocumento);
  }

  remover(id: number): Promise<AxiosResponse> {
    return api.delete('/BibDocumento/' + id);
  }

  atualizar(bibDocumento: BibDocumentoModel): Promise<AxiosResponse> {
    return api.put('/BibDocumento', bibDocumento);
  }

  async get(id: number): Promise<BibDocumentoModel> {
    const result = await api.get<BibDocumentoModel>(`/BibDocumento/${id.toString()}`);
    return result.data;
  }

  async Marc21(textoMarc: string): Promise<BibDocumentoMarcModel> {
    const result = await api.post<BibDocumentoMarcModel>('/BibDocumento/Marc21', {
      textoMarc21: textoMarc
    });
    //console.log(Object.keys(result.data as any));
    return result.data;
  }
}


export default new BibDocumentoService();
